import { type ComponentType, useEffect } from 'react'

import { InnosErrorBoundary } from '@inno-tools/container-bridge'
import { Spinner } from '@inno-ui/spinner'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { useFederatedComponent } from 'modules/app-registry/hooks/useFederatedComponent'
import { AppModals, UsercentricsQuickAction } from 'modules/desktop/components'
import { SttQuickAction } from 'modules/desktop/modules/stt'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'
import { appConfigs } from 'shared/configs/appConfigs'
import { INNOSCRIPTA_INTERNAL_ORGANIZATION_ID } from 'shared/constants/user'
import { useUserContext } from 'shared/hooks'
import { useCommonStore } from 'shared/stores/commonStore'
import type { User } from 'shared/types/user'
import { MainLayout } from 'src/shared/components'
import { useTopDockHeight } from 'src/shared/components/MainLayout/hooks/useTopDockHeight'
import { getIsInsideWindow } from 'src/shared/utils'

import styles from './MyInvoicesPage.module.scss'

// Accounting exposes a plain React component here, not the widget mountApp
// contract. See accounting/src/MyInvoicesApp.tsx.
type MyInvoicesModule = {
  MyInvoicesApp: ComponentType<{ user: User }>
}

const MyInvoicesPageContent = ({ user }: { user: User }) => {
  const { module, isLoaded, isError, missingRemoteUrl } = useFederatedComponent(
    process.env.REACT_APP_ACCOUNTING_APP_DEV_PATH,
    'accounting',
    './MyInvoicesApp',
    'accounting',
    undefined,
    process.env.REACT_APP_ACCOUNTING_APP_CDN_URL
  )

  const RemoteMyInvoicesApp =
    isLoaded && !isError && !missingRemoteUrl && module
      ? (module as unknown as MyInvoicesModule).MyInvoicesApp
      : undefined

  if (!RemoteMyInvoicesApp) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
      </div>
    )
  }

  return <RemoteMyInvoicesApp user={user} />
}

export const MyInvoicesPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useUserContext()
  const { height: topDockHeight, topDockRef } = useTopDockHeight()
  const isWindowMode = getIsInsideWindow()
  const setIsDetached = useCommonStore(state => state.setIsDetached)
  const { apps: availableApps } = useAvailableApps()

  useEffect(() => {
    setIsDetached(true)
    return () => setIsDetached(false)
  }, [setIsDetached])

  if (!user) return null
  if (user.organization_id === INNOSCRIPTA_INTERNAL_ORGANIZATION_ID) {
    return <Navigate to='/' replace />
  }

  return (
    <MainLayout
      topDockHeight={topDockHeight}
      topDockRef={topDockRef}
      quickActions={
        <>
          <SttQuickAction />
          <UsercentricsQuickAction />
        </>
      }
      handleAppClick={app => {
        if (
          Object.values(availableApps).some(
            availableApp =>
              availableApp.id === app.id && availableApp.isDisabled
          )
        ) {
          return
        }

        const relativePath = app.url!.replace(
          process.env.REACT_APP_CONTAINER_DOMAIN!,
          ''
        )
        navigate(relativePath)
      }}
    >
      <div className={styles.content}>
        <InnosErrorBoundary
          debugName='MyInvoicesPage'
          loadingComponent={
            <div className={styles.loadingContainer}>
              <Spinner />
            </div>
          }
          fallbackComponent={() => (
            <div role='alert'>
              {t('common.loadingError', { appName: 'My Bills' })}
            </div>
          )}
        >
          <MyInvoicesPageContent user={user} />
        </InnosErrorBoundary>
      </div>

      {!isWindowMode &&
        Object.entries(appConfigs).map(([id, config]) =>
          config?.modals ? (
            <AppModals key={`${id}-modals`} appConfig={config} />
          ) : null
        )}
    </MainLayout>
  )
}
